import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import KpiCard from "../components/KpiCard";
import { api, fmtUSD, fmtPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, LineChart, Line, Legend,
} from "recharts";
import { AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const statusBadge = (s) => {
  const map = {
    green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    yellow: "bg-amber-100 text-amber-800 border-amber-200",
    red: "bg-red-100 text-red-800 border-red-200",
  };
  return map[s] || "bg-slate-100 text-slate-700";
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [studies, setStudies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [kpis, studiesR, statsR] = await Promise.all([
        api.get("/dashboard/kpis").then((r) => r.data),
        api.get("/pnl/studies/all").then((r) => r.data),
        api.get("/admin/stats").then((r) => r.data),
      ]);
      setData(kpis);
      setStudies(studiesR);
      setStats(statsR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const seed = async () => {
    if (stats?.has_data) {
      if (!window.confirm("⚠️  This will WIPE all existing CTAs, revenues, expenses, timesheets and replace with demo data. Export your data first! Continue?")) {
        return;
      }
    }
    toast.info("Seeding demo data...");
    try {
      await api.post("/seed/demo?confirm=YES");
      toast.success("Demo data loaded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    }
  };

  const forecastData = data
    ? [
        { period: "30d", amount: data.cash_forecast_30 },
        { period: "60d", amount: data.cash_forecast_60 },
        { period: "90d", amount: data.cash_forecast_90 },
      ]
    : [];

  return (
    <AppShell
      title="Executive Dashboard"
      subtitle="Real-time financial picture across all clinical research sites"
      actions={
        stats?.demo_seed_enabled && (
          <Button
            variant="outline"
            onClick={seed}
            data-testid="seed-demo-btn"
            title={stats?.has_data ? "Real data exists — this will wipe it" : "Populate with sample data"}
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {stats?.has_data ? "Reset to Demo" : "Seed Demo Data"}
          </Button>
        )
      }
    >
      {!data && <div className="text-slate-500">{loading ? "Loading..." : "No data yet"}</div>}
      {data && (
        <div className="space-y-6">
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard testid="kpi-total-ar" label="Total AR Outstanding" value={fmtUSD(data.total_ar_outstanding)} sub={`YTD: ${fmtUSD(data.ytd_ar_outstanding)}`} accent="#F05D23" />
            <KpiCard testid="kpi-invoiced-ytd" label="Invoiced YTD" value={fmtUSD(data.invoiced_ytd)} sub={`MTD: ${fmtUSD(data.invoiced_mtd)}`} accent="#0A4C95" />
            <KpiCard testid="kpi-collected-ytd" label="Collected YTD" value={fmtUSD(data.collected_ytd)} sub={`MTD: ${fmtUSD(data.collected_mtd)}`} trend="up" accent="#10B981" />
            <KpiCard testid="kpi-net-revenue" label="Net Revenue YTD" value={fmtUSD(data.net_revenue_ytd)} sub="Collected (net)" accent="#0A4C95" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cash forecast */}
            <div className="bg-white border border-border rounded-md p-6 shadow-sm lg:col-span-1">
              <h3 className="text-base font-semibold text-slate-900">Cash Forecast</h3>
              <p className="text-xs text-slate-500 mt-0.5">Expected collections from outstanding AR</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={forecastData} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 12, fill: "#6B7280" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtUSD(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar dataKey="amount" fill="#0A4C95" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Site EBITDA status list */}
            <div className="bg-white border border-border rounded-md p-6 shadow-sm lg:col-span-2" data-testid="site-ebitda-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Site EBITDA Status</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Green ≥ 20% · Yellow 5–20% · Red &lt; 5%</p>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {data.site_ebitda.length === 0 && <div className="text-sm text-slate-500 py-4">No sites yet. Seed demo data to explore.</div>}
                {data.site_ebitda.map((s) => (
                  <div key={s.site_id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-[14px] text-slate-900">{s.site_name}</div>
                      <div className="text-xs text-slate-500">PI: {s.pi_name} · Revenue: <span className="font-mono-fin">{fmtUSD(s.gross_revenue)}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono-fin font-semibold text-slate-900">{fmtPct(s.ebitda_percent)}</span>
                      <Badge variant="outline" className={statusBadge(s.status)}>{s.status.toUpperCase()}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top overdue + alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid="overdue-ar-panel">
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" style={{ color: "#F05D23" }} /> Top Overdue AR
              </h3>
              <div className="mt-4 space-y-2">
                {data.top_overdue_ar.length === 0 && <div className="text-sm text-slate-500">No overdue items.</div>}
                {data.top_overdue_ar.map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-2.5 px-3 rounded bg-red-50 border border-red-100">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{fmtUSD(o.amount)}</div>
                      <div className="text-xs text-slate-500">Due {o.due_date} · {o.days_overdue} days overdue</div>
                    </div>
                    <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">OVERDUE</Badge>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid="invoice-alerts-panel">
              <h3 className="text-base font-semibold text-slate-900">Invoice Submission Alerts</h3>
              <p className="text-xs text-slate-500 mt-0.5">Visits completed &gt; 14 days ago with no invoice</p>
              <div className="mt-4 space-y-2">
                {data.invoice_alerts.length === 0 && <div className="text-sm text-slate-500">All invoices up to date.</div>}
                {data.invoice_alerts.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-2.5 px-3 rounded bg-amber-50 border border-amber-100">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{fmtUSD(a.amount)}</div>
                      <div className="text-xs text-slate-500">Visit {a.visit_completion_date} · {a.days_since}d ago</div>
                    </div>
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">PENDING</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Study ROI ranking */}
          <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid="study-roi-panel">
            <h3 className="text-base font-semibold text-slate-900">Study Profitability Snapshot</h3>
            <div className="overflow-auto mt-4">
              <table className="w-full text-sm stryde-table">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2">Study</th>
                    <th className="text-left px-3 py-2">Sponsor</th>
                    <th className="text-right px-3 py-2">Revenue</th>
                    <th className="text-right px-3 py-2">Net Profit</th>
                    <th className="text-right px-3 py-2">Gross Margin</th>
                    <th className="text-right px-3 py-2">ROI</th>
                  </tr>
                </thead>
                <tbody>
                  {studies.map((s) => (
                    <tr key={s.cta_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-900">{s.study_name}</td>
                      <td className="px-3 py-2 text-slate-600">{s.sponsor}</td>
                      <td className="px-3 py-2 text-right font-mono-fin">{fmtUSD(s.gross_revenue)}</td>
                      <td className={`px-3 py-2 text-right font-mono-fin ${s.net_profit >= 0 ? "text-slate-900" : "text-red-600"}`}>{fmtUSD(s.net_profit)}</td>
                      <td className="px-3 py-2 text-right font-mono-fin">
                        <Badge variant="outline" className={s.gross_margin_percent >= 50 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : s.gross_margin_percent >= 20 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200"}>{fmtPct(s.gross_margin_percent)}</Badge>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-fin font-semibold">{fmtPct(s.roi_score)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
